import React from 'react';
import {
  ChatTextAreaInternal as ChatTextArea,
  GoToBottomButton,
  UpgradeCtaCard,
  useFileAttachments,
} from '@ideoz/ui';

type ChatProps = React.ComponentProps<typeof ChatTextArea>;

interface ConversationComposerProps {
  containerRef: React.Ref<HTMLDivElement>;

  /** Interactive question set, rendered inside the sticky container just above
   *  the input so it stays pinned to the composer. */
  questionSlot?: React.ReactNode;

  // Scroll-to-bottom button
  showScrollButton: boolean;
  scrollButtonOpacity: number;
  onScrollToBottom: () => void;

  /** Gate: when false the upgrade CTA replaces the input. */
  canProceed: boolean;
  hasBaseCredits: boolean;
  onUpgrade: () => void;

  // Chat input
  onSubmit: ChatProps['onSubmit'];
  conversationId: ChatProps['conversationId'];
  onEnsureConversationId: ChatProps['onEnsureConversationId'];
  currentMode: ChatProps['currentMode'];
  isLoading: boolean;
  isStreaming: boolean;
  isPrototypeCreating: boolean;
  onCancel: () => void;
  isWeb: boolean;
  llmFamily: ChatProps['initialLlmFamily'];
  onLlmFamilyChange: ChatProps['onLlmFamilyChange'];
  isPrototypeOpen: boolean;
  onGeneratePrototype: ChatProps['onGeneratePrototype'];
  onBackToUncover: ChatProps['onBackToUncover'];
  hasPrototypeCredits: boolean;
  hasProject: boolean;
  initiatedFromUncover: ChatProps['initiatedFromUncover'];
  prototyperGenerationModel: ChatProps['prototyperGenerationModel'];
  onPrototyperGenerationModelChange: ChatProps['onPrototyperGenerationModelChange'];
  onFileUploaded: ChatProps['onFileUploaded'];
}

/** Dumb: the sticky bottom input area (chat composer or upgrade CTA). */
export function ConversationComposer({
  containerRef,
  questionSlot,
  showScrollButton,
  scrollButtonOpacity,
  onScrollToBottom,
  canProceed,
  hasBaseCredits,
  onUpgrade,
  onSubmit,
  conversationId,
  onEnsureConversationId,
  currentMode,
  isLoading,
  isStreaming,
  isPrototypeCreating,
  onCancel,
  isWeb,
  llmFamily,
  onLlmFamilyChange,
  isPrototypeOpen,
  onGeneratePrototype,
  onBackToUncover,
  hasPrototypeCredits,
  hasProject,
  initiatedFromUncover,
  prototyperGenerationModel,
  onPrototyperGenerationModelChange,
  onFileUploaded,
}: ConversationComposerProps): JSX.Element {
  return (
    <div
      ref={containerRef}
      className="bg-background transition-all duration-200 ease-in-out sticky bottom-0 pb-2"
    >
      {questionSlot && (
        <div className="mx-auto w-full max-w-3xl px-3 pb-2 relative z-50">{questionSlot}</div>
      )}
      <div className="relative">
        {/* Scroll to Bottom Button */}
        <div className="absolute -top-12 flex justify-center w-full">
          <GoToBottomButton
            isVisible={showScrollButton}
            opacity={scrollButtonOpacity}
            onClick={onScrollToBottom}
          />
        </div>
        {canProceed ? (
          <ChatTextArea
            onSubmit={onSubmit}
            conversationId={conversationId}
            onEnsureConversationId={onEnsureConversationId}
            placeholder={
              currentMode === 'prototyper'
                ? 'Describe your user experience prototype to generate …'
                : 'Type anything to Nexus …'
            }
            disabled={isLoading || isPrototypeCreating}
            isChatLoading={isLoading || isStreaming || isPrototypeCreating}
            onCancel={onCancel}
            useFileAttachmentsHook={useFileAttachments}
            isWeb={isWeb}
            initialLlmFamily={llmFamily}
            onLlmFamilyChange={onLlmFamilyChange}
            data-testid="conversation-chat-textarea"
            isPrototypeOpen={isPrototypeOpen}
            rows={1}
            isPrototypeCreating={isPrototypeCreating}
            currentMode={currentMode}
            onGeneratePrototype={onGeneratePrototype}
            onBackToUncover={onBackToUncover}
            hasPrototypeCredits={hasPrototypeCredits}
            hasProject={hasProject}
            initiatedFromUncover={initiatedFromUncover}
            prototyperGenerationModel={prototyperGenerationModel}
            onPrototyperGenerationModelChange={onPrototyperGenerationModelChange}
            onFileUploaded={onFileUploaded}
          />
        ) : (
          <UpgradeCtaCard
            title={
              !hasBaseCredits
                ? "You've used all your credits"
                : "You've used all your prototype credits"
            }
            description={
              !hasBaseCredits
                ? 'Upgrade your plan or purchase additional credits to continue.'
                : 'Upgrade your plan or purchase additional prototype credits to continue.'
            }
            buttonText="Upgrade"
            onUpgrade={onUpgrade}
          />
        )}
      </div>
    </div>
  );
}
